import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link, Navigate } from 'react-router-dom';
import { z } from 'zod';
import { useAuth } from '@/hooks/useAuth';
import { useInvitation } from '@/hooks/useInvitation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Mail, Eye, EyeOff, Users, Loader2, AlertCircle } from 'lucide-react';
import { PasswordStrengthIndicator, PASSWORD_MIN_LENGTH, SPECIAL_CHAR_REGEX } from '@/components/auth/PasswordStrengthIndicator';
import { PasswordGeneratorButton } from '@/components/auth/PasswordGenerator';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { translateAuthError } from '@/lib/authErrors';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { CenteredSpinner } from '@/components/ui/centered-spinner';
import { GoogleAuthButton } from '@/components/auth/GoogleAuthButton';

const emailSchema = z.object({
  email: z.string().trim().email({ message: "Adresse email invalide" }),
});

const loginSchema = z.object({
  email: z.string().trim().email({ message: "Adresse email invalide" }),
  password: z.string().min(1, { message: "Le mot de passe est requis" }),
});

const passwordSchema = z.object({
  password: z.string()
    .min(PASSWORD_MIN_LENGTH, { message: `Le mot de passe doit contenir au moins ${PASSWORD_MIN_LENGTH} caractères` })
    .refine((pwd) => /[A-Z]/.test(pwd), { message: "Le mot de passe doit contenir une majuscule" })
    .refine((pwd) => /[a-z]/.test(pwd), { message: "Le mot de passe doit contenir une minuscule" })
    .refine((pwd) => /[0-9]/.test(pwd), { message: "Le mot de passe doit contenir un chiffre" })
    .refine((pwd) => SPECIAL_CHAR_REGEX.test(pwd), { message: "Le mot de passe doit contenir un caractère spécial" }),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Les mots de passe ne correspondent pas",
  path: ["confirmPassword"],
});

type AuthMode = 'login' | 'forgot' | 'reset' | 'magic';

const AUTH_TITLES: Record<AuthMode, string> = {
  login: 'Connexion',
  forgot: 'Mot de passe oublié',
  reset: 'Nouveau mot de passe',
  magic: 'Connexion par email',
};

const Auth = () => {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string; confirmPassword?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isAcceptingInvitation, setIsAcceptingInvitation] = useState(false);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  
  const { user, loading, signIn, resetPassword, updatePassword, signInWithMagicLink, signInWithGoogle } = useAuth();
  const { invitation, isLoading: invitationLoading, acceptInvitation } = useInvitation();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const hasInviteParams = searchParams.has('invite') && searchParams.has('token');
  
  // Dynamic page title based on auth mode
  usePageTitle(invitation ? `Rejoindre ${invitation.workspaceName}` : AUTH_TITLES[mode]);

  // SEO meta tags
  useDocumentHead({
    title: invitation 
      ? `Rejoindre ${invitation.workspaceName} - Luminjo`
      : `${AUTH_TITLES[mode]} - Luminjo`,
    description: 'Connectez-vous à votre espace de support client Luminjo. Service client simple et intuitif.',
    noindex: mode === 'reset', // Don't index password reset pages
  });

  // Pre-fill email from invitation
  useEffect(() => {
    if (invitation?.email && !email) {
      setEmail(invitation.email);
    }
  }, [invitation?.email, email]);

  useEffect(() => {
    // Check if we're in reset mode from URL
    if (searchParams.get('mode') === 'reset') {
      setMode('reset');
    }
  }, [searchParams]);

  // Handle logged-in user with invitation
  // Tries to accept the invitation if valid, OR re-attach the membership if the
  // invitation was already accepted (covers users clicking the link twice or
  // landing here after a signup that lost the membership row).
  const canAutoAccept = !!invitation && !invitation.isExpired;
  useEffect(() => {
    const handleLoggedInUser = async () => {
      if (invitationError || isAcceptingInvitation) return;

      if (user && !loading && canAutoAccept && mode !== 'reset') {
        setIsAcceptingInvitation(true);

        const result = await acceptInvitation();

        if (result.success) {
          toast({
            title: 'Bienvenue !',
            description: `Vous avez rejoint l'espace "${invitation!.workspaceName}".`,
          });
          navigate('/', { replace: true });
        } else {
          setInvitationError(result.error || 'Impossible de rejoindre l\'espace.');
          setIsAcceptingInvitation(false);
        }
      } else if (user && !loading && mode !== 'reset' && !hasInviteParams) {
        navigate('/');
      }
    };

    handleLoggedInUser();
  }, [user, loading, invitation, canAutoAccept, hasInviteParams, acceptInvitation, navigate, toast, isAcceptingInvitation, mode, invitationError]);

  const validateForm = () => {
    if (mode === 'forgot' || mode === 'magic') {
      const result = emailSchema.safeParse({ email });
      if (!result.success) {
        setErrors({ email: result.error.errors[0]?.message });
        return false;
      }
      setErrors({});
      return true;
    }

    if (mode === 'reset') {
      const result = passwordSchema.safeParse({ password, confirmPassword });
      if (!result.success) {
        const fieldErrors: { password?: string; confirmPassword?: string } = {};
        result.error.errors.forEach((err) => {
          if (err.path[0] === 'password') fieldErrors.password = fieldErrors.password || err.message;
          if (err.path[0] === 'confirmPassword') fieldErrors.confirmPassword = err.message;
        });
        setErrors(fieldErrors);
        return false;
      }
      setErrors({});
      return true;
    }

    const result = loginSchema.safeParse({ email, password });
    if (!result.success) {
      const fieldErrors: { email?: string; password?: string } = {};
      result.error.errors.forEach((err) => {
        if (err.path[0] === 'email') fieldErrors.email = err.message;
        if (err.path[0] === 'password') fieldErrors.password = err.message;
      });
      setErrors(fieldErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);

    try {
      if (mode === 'magic') {
        // Preserve invitation params so the user lands back on this page after
        // clicking the magic link and the auto-accept flow can complete.
        const magicRedirect = invitation?.isValid
          ? `${window.location.origin}/auth?invite=${invitation.workspaceId}&token=${invitation.token}`
          : undefined;
        const { error } = await signInWithMagicLink(email, magicRedirect);
        if (error) {
          toast({
            title: "Erreur",
            description: translateAuthError(error.message),
            variant: "destructive",
          });
        } else {
          toast({
            title: "Email envoyé !",
            description: "Vérifiez votre boîte mail pour vous connecter.",
          });
        }
      } else if (mode === 'forgot') {
        const { error } = await resetPassword(email);
        if (error) {
          toast({
            title: "Erreur",
            description: translateAuthError(error.message),
            variant: "destructive",
          });
        } else {
          toast({
            title: "Email envoyé !",
            description: "Vérifiez votre boîte mail pour réinitialiser votre mot de passe.",
          });
          setMode('login');
        }
      } else if (mode === 'reset') {
        const { error } = await updatePassword(password);
        if (error) {
          toast({
            title: "Erreur",
            description: translateAuthError(error.message),
            variant: "destructive",
          });
        } else {
          toast({
            title: "Mot de passe mis à jour !",
            description: "Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.",
          });
          navigate('/');
        }
      } else if (mode === 'login') {
        const { error } = await signIn(email, password);
        if (error) {
          toast({
            title: "Erreur de connexion",
            description: translateAuthError(error.message),
            variant: "destructive",
          });
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTitle = () => {
    if (invitation?.isValid && mode === 'login') return 'Connexion';
    switch (mode) {
      case 'login': return 'Connexion';
      case 'forgot': return 'Mot de passe oublié';
      case 'reset': return 'Nouveau mot de passe';
      case 'magic': return 'Connexion par email';
    }
  };

  const getSubtitle = () => {
    if (invitation?.isValid && mode === 'login') {
      return `Connectez-vous pour rejoindre ${invitation.workspaceName}`;
    }
    switch (mode) {
      case 'login': return 'Accédez à votre espace de support client';
      case 'forgot': return 'Entrez votre email pour recevoir un lien de réinitialisation';
      case 'reset': return 'Choisissez un nouveau mot de passe sécurisé';
      case 'magic': return 'Recevez un lien de connexion sécurisé par email';
    }
  };

  const getButtonText = () => {
    if (isSubmitting) return 'Chargement...';
    if (invitation?.isValid && mode === 'login') return 'Se connecter et rejoindre';
    switch (mode) {
      case 'login': return 'Se connecter';
      case 'forgot': return 'Envoyer le lien';
      case 'reset': return 'Mettre à jour';
      case 'magic': return 'Envoyer le lien magique';
    }
  };

  // Loading state
  if (loading || invitationLoading) {
    return <CenteredSpinner />;
  }

  // Expired invitation: redirect to dedicated screen with reissue request flow
  if (invitation?.isExpired) {
    const inviteId = searchParams.get('invite');
    const tokenParam = searchParams.get('token');
    return (
      <Navigate
        to={`/invitation/expired?invite=${inviteId}&token=${tokenParam}`}
        replace
      />
    );
  }

  // Show invitation error for logged-in users with email mismatch
  if (user && canAutoAccept && invitationError) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center justify-center py-8">
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Impossible de rejoindre l'espace</AlertTitle>
            <AlertDescription className="mt-2">
              {invitationError}
            </AlertDescription>
          </Alert>
          
          <p className="text-muted-foreground text-center mb-4">
            Vous êtes connecté(e) en tant que <strong>{user.email}</strong>
          </p>
          
          <div className="flex flex-col gap-2 w-full">
            <Button 
              onClick={async () => {
                await supabase.auth.signOut();
                setInvitationError(null);
              }}
              className="w-full"
            >
              Se déconnecter et utiliser un autre compte
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/', { replace: true })}
              className="w-full"
            >
              Continuer vers le tableau de bord
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Show accepting invitation state for logged-in users
  if (user && canAutoAccept && isAcceptingInvitation) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <h2 className="text-xl font-semibold mb-2">Validation de l'invitation...</h2>
          <p className="text-muted-foreground text-center">
            Vous rejoignez l'espace <strong>{invitation.workspaceName}</strong>
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {(mode === 'forgot' || mode === 'reset' || mode === 'magic') && (
        <button
          type="button"
          onClick={() => setMode('login')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à la connexion
        </button>
      )}

      {/* Invitation context banner */}
      {invitation?.isValid && mode === 'login' && (
        <Alert className="mb-6 border-primary/30 bg-primary/5">
          <Users className="h-4 w-4 text-primary" />
          <AlertTitle className="text-primary">Vous êtes invité(e) !</AlertTitle>
          <AlertDescription>
            Connectez-vous pour rejoindre l'équipe <strong>{invitation.workspaceName}</strong>.
          </AlertDescription>
        </Alert>
      )}

      <h1 className="text-3xl font-bold text-foreground mb-2">
        {getTitle()}
      </h1>
      <p className="text-muted-foreground mb-8">
        {getSubtitle()}
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        {(mode === 'login' || mode === 'forgot' || mode === 'magic') && (
          <div className="space-y-2">
            <Label htmlFor="email">Adresse email</Label>
            <Input
              id="email"
              type="email"
              placeholder="vous@exemple.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={errors.email ? 'border-destructive' : ''}
              disabled={isSubmitting}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email}</p>
            )}
          </div>
        )}

        {(mode === 'login' || mode === 'reset') && (
          <div className="space-y-2">
            <Label htmlFor="password">
              {mode === 'reset' ? 'Nouveau mot de passe' : 'Mot de passe'}
            </Label>
            <div className="relative flex gap-1">
              <div className="relative flex-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={errors.password ? 'border-destructive pr-10' : 'pr-10'}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {mode === 'reset' && (
                <PasswordGeneratorButton 
                  onGenerate={(pwd) => {
                    setPassword(pwd);
                    setShowPassword(true);
                    setErrors((prev) => ({ ...prev, password: undefined }));
                  }}
                  disabled={isSubmitting}
                />
              )}
            </div>
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password}</p>
            )}
            {mode === 'reset' && (
              <PasswordStrengthIndicator password={password} />
            )}
          </div>
        )}

        {mode === 'reset' && (
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={errors.confirmPassword ? 'border-destructive pr-10' : 'pr-10'}
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showConfirmPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-destructive">{errors.confirmPassword}</p>
            )}
          </div>
        )}

        {mode === 'login' && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                setMode('forgot');
                setErrors({});
              }}
              className="text-primary hover:text-primary/80 text-sm font-medium transition-colors"
              disabled={isSubmitting}
            >
              Mot de passe oublié ?
            </button>
          </div>
        )}

        <Button 
          type="submit" 
          className="w-full" 
          size="lg"
          disabled={isSubmitting}
        >
          {getButtonText()}
        </Button>

        {mode === 'login' && (
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">ou</span>
            </div>
          </div>
        )}

        {mode === 'login' && (
          <Button
            type="button"
            variant="outline"
            className="w-full"
            size="lg"
            onClick={() => {
              setMode('magic');
              setErrors({});
            }}
            disabled={isSubmitting}
          >
            <Mail className="mr-2 h-4 w-4" />
            Connexion par lien magique
          </Button>
        )}

        {mode === 'login' && (
          <GoogleAuthButton
            mode="signin"
            onClick={async () => {
              // Preserve invitation params across the OAuth round-trip so
              // useInvitation can pick them up and accept on return.
              const googleRedirect = invitation?.isValid
                ? `${window.location.origin}/auth?invite=${invitation.workspaceId}&token=${invitation.token}`
                : undefined;
              const { error } = await signInWithGoogle(googleRedirect);
              if (error) {
                toast({
                  title: "Erreur",
                  description: "Impossible de se connecter avec Google",
                  variant: "destructive",
                });
              }
            }}
            disabled={isSubmitting}
          />
        )}
      </form>

      {mode === 'login' && (
        <div className="mt-6 text-center">
          {invitation?.isValid ? (
            <p className="text-sm text-muted-foreground">
              Pas encore de compte ?{' '}
              <Link
                to={`/signup?invite=${invitation.workspaceId}&token=${invitation.token}`}
                className="text-primary hover:text-primary/80 font-medium transition-colors"
              >
                Créer un compte
              </Link>
            </p>
          ) : (
            <Link
              to="/signup"
              className="text-primary hover:text-primary/80 text-sm font-medium transition-colors"
            >
              Pas encore de compte ? S'inscrire
            </Link>
          )}
        </div>
      )}
    </AuthLayout>
  );
};

export default Auth;
